import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  Spinner,
  Checkbox,
  Flex,
  Text,
  Input,
  Select,
  HStack,
  useColorModeValue,
  VStack,
  Badge,
  InputGroup,
  InputLeftElement,
  Icon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import { SearchIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { getToken } from '../../../../services/api';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? process.env.REACT_APP_VERCEL_API_URL 
  : process.env.REACT_APP_LOCAL_API_URL;

const PrecatoriosSP = () => {
  const [precatorios, setPrecatorios] = useState([]);
  const [filteredPrecatorios, setFilteredPrecatorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('numero_precatorio');
  const [sortField, setSortField] = useState('ordem');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  
  const toast = useToast();
  const navigate = useNavigate();
  
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const headingColor = useColorModeValue('gray.700', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const theadBg = useColorModeValue('gray.50', 'gray.900');

  useEffect(() => {
    fetchPrecatorios();
  }, []);

  useEffect(() => {
    filterAndSortPrecatorios();
  }, [precatorios, searchTerm, filterField, sortField, sortOrder]);

  const fetchPrecatorios = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/precatorioSP`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar precatórios');
      }

      const data = await response.json();
      setPrecatorios(data.data || []);
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao carregar precatórios',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortPrecatorios = () => {
    let filtered = [...precatorios];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item => {
        const value = item[filterField]?.toString().toLowerCase() || '';
        return value.includes(searchTerm.toLowerCase());
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle numeric fields
      if (sortField === 'valor_atualizado' || sortField === 'ordem') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredPrecatorios(filtered);
    setCurrentPage(1);
  };

  const handleSelectItem = (id) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const pageItems = getCurrentPageItems();
      const newSelected = new Set(selectedItems);
      pageItems.forEach(item => newSelected.add(item._id));
      setSelectedItems(newSelected);
    } else {
      const pageItems = getCurrentPageItems();
      const newSelected = new Set(selectedItems);
      pageItems.forEach(item => newSelected.delete(item._id));
      setSelectedItems(newSelected);
    }
  };

  const handleUpdateSelection = async (selected) => {
    if (selectedItems.size === 0) {
      toast({
        title: 'Nenhum item selecionado',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/precatorioSP/update-selection`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ids: Array.from(selectedItems),
          selected
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar seleção');
      }

      const result = await response.json();
      
      toast({
        title: 'Seleção atualizada',
        description: result.message,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Clear selection and refresh
      setSelectedItems(new Set());
      fetchPrecatorios();
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao atualizar seleção',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleFileUpload = async (event, isPriority = false) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = getToken();
      const endpoint = isPriority ? 'upload-prioridade' : 'upload';
      const response = await fetch(`${API_BASE_URL}/precatorioSP/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Erro ao fazer upload do arquivo');
      }

      const result = await response.json();
      
      toast({
        title: 'Upload realizado com sucesso',
        description: result.message,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      fetchPrecatorios();
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao fazer upload',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }

    // Reset file input
    event.target.value = '';
  };

  const handleDownloadSelected = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/precatorioSP/download-selected`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao baixar arquivo');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'precatorios_selecionados_sp.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erro:', error);
      toast({
        title: 'Erro ao baixar arquivo',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredPrecatorios.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filteredPrecatorios.length / itemsPerPage);

  const getStatusColor = (selecionado) => {
    return selecionado ? 'green' : 'gray';
  };

  // Calculate statistics
  const selectedCount = precatorios.filter(p => p.selecionado).length;
  const totalValue = precatorios.reduce((sum, p) => sum + (p.valor_atualizado || 0), 0);
  const selectedValue = precatorios.filter(p => p.selecionado).reduce((sum, p) => sum + (p.valor_atualizado || 0), 0);

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Text fontSize="3xl" fontWeight="bold" color={headingColor} mb={2}>
            Precatórios São Paulo
          </Text>
          <Text color={textColor}>
            Gerencie os precatórios do estado de São Paulo
          </Text>
        </Box>

        {/* Statistics */}
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
          <Stat bg={bg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
            <StatLabel>Total de Precatórios</StatLabel>
            <StatNumber>{precatorios.length}</StatNumber>
            <StatHelpText>Registrados no sistema</StatHelpText>
          </Stat>
          <Stat bg={bg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
            <StatLabel>Selecionados</StatLabel>
            <StatNumber>{selectedCount}</StatNumber>
            <StatHelpText>{((selectedCount / precatorios.length) * 100).toFixed(1)}%</StatHelpText>
          </Stat>
          <Stat bg={bg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
            <StatLabel>Valor Total</StatLabel>
            <StatNumber fontSize="lg">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalValue)}
            </StatNumber>
          </Stat>
          <Stat bg={bg} p={4} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
            <StatLabel>Valor Selecionado</StatLabel>
            <StatNumber fontSize="lg">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedValue)}
            </StatNumber>
          </Stat>
        </SimpleGrid>

        {/* Actions */}
        <Box bg={bg} p={6} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="lg" fontWeight="semibold">Ações</Text>
            <HStack spacing={4} flexWrap="wrap">
              <Button
                colorScheme="blue"
                onClick={() => navigate('/admin/precatorios/sp/selecionadas')}
              >
                Ver Selecionados
              </Button>
              <Button as="label" colorScheme="green" cursor="pointer">
                Upload Regular
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleFileUpload(e, false)}
                  display="none"
                />
              </Button>
              <Button as="label" colorScheme="purple" cursor="pointer">
                Upload Prioridade
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleFileUpload(e, true)}
                  display="none"
                />
              </Button>
              <Button colorScheme="teal" onClick={handleDownloadSelected}>
                Download Selecionados
              </Button>
            </HStack>
          </VStack>
        </Box>

        {/* Filters */}
        <Box bg={bg} p={6} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="lg" fontWeight="semibold">Filtros e Busca</Text>
            <HStack spacing={4}>
              <InputGroup maxW="400px">
                <InputLeftElement pointerEvents="none">
                  <Icon as={SearchIcon} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              <Select
                maxW="200px"
                value={filterField}
                onChange={(e) => setFilterField(e.target.value)}
              >
                <option value="numero_precatorio">Número</option>
                <option value="beneficiario">Beneficiário</option>
                <option value="cpf_cnpj">CPF/CNPJ</option>
                <option value="ente">Ente</option>
              </Select>
              <Select
                maxW="200px"
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
              >
                <option value="ordem">Ordem</option>
                <option value="valor_atualizado">Valor</option>
                <option value="numero_precatorio">Número</option>
                <option value="beneficiario">Beneficiário</option>
              </Select>
              <Button
                size="sm"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </Button>
            </HStack>
            <HStack spacing={4}>
              <Text>
                {selectedItems.size} selecionados | Página {currentPage} de {totalPages}
              </Text>
              <Button
                size="sm"
                colorScheme="green"
                leftIcon={<CheckIcon />}
                onClick={() => handleUpdateSelection(true)}
                isDisabled={selectedItems.size === 0}
              >
                Marcar {selectedItems.size} como Selecionados
              </Button>
            </HStack>
          </VStack>
        </Box>

        {/* Table */}
        <Box bg={bg} borderRadius="lg" borderWidth="1px" borderColor={borderColor} overflow="hidden">
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg={theadBg}>
                <Tr>
                  <Th width="50px">
                    <Checkbox
                      isChecked={
                        getCurrentPageItems().length > 0 &&
                        getCurrentPageItems().every(item => selectedItems.has(item._id))
                      }
                      onChange={handleSelectAll}
                    />
                  </Th>
                  <Th>Ordem</Th>
                  <Th>Número</Th>
                  <Th>Ente</Th>
                  <Th>Beneficiário</Th>
                  <Th>Natureza</Th>
                  <Th>Orçamento</Th>
                  <Th>Prioridade</Th>
                  <Th isNumeric>Valor</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {getCurrentPageItems().map((item) => (
                  <Tr key={item._id} _hover={{ bg: hoverBg }}>
                    <Td>
                      <Checkbox
                        isChecked={selectedItems.has(item._id)}
                        onChange={() => handleSelectItem(item._id)}
                      />
                    </Td>
                    <Td>{item.ordem}</Td>
                    <Td>{item.numero_precatorio}</Td>
                    <Td>{item.ente}</Td>
                    <Td>{item.beneficiario}</Td>
                    <Td>{item.natureza}</Td>
                    <Td>{item.orcamento}</Td>
                    <Td>{item.prioridade || '-'}</Td>
                    <Td isNumeric>
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(item.valor_atualizado || 0)}
                    </Td>
                    <Td>
                      <Badge colorScheme={getStatusColor(item.selecionado)}>
                        {item.selecionado ? 'Selecionado' : 'Não Selecionado'}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pagination */}
        <HStack justify="center" spacing={2}>
          <Button
            size="sm"
            onClick={() => setCurrentPage(1)}
            isDisabled={currentPage === 1}
          >
            Primeira
          </Button>
          <Button
            size="sm"
            onClick={() => setCurrentPage(currentPage - 1)}
            isDisabled={currentPage === 1}
          >
            Anterior
          </Button>
          <Text>
            Página {currentPage} de {totalPages}
          </Text>
          <Button
            size="sm"
            onClick={() => setCurrentPage(currentPage + 1)}
            isDisabled={currentPage === totalPages}
          >
            Próxima
          </Button>
          <Button
            size="sm"
            onClick={() => setCurrentPage(totalPages)}
            isDisabled={currentPage === totalPages}
          >
            Última
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default PrecatoriosSP;